import React, { useMemo } from "react";
import Modal from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import {
  Model,
  getModelNameKey,
  getModelFieldKey,
  getFieldNameKey,
  getFieldDescriptionKey,
  getFieldTypeKey,
  getFieldOptionsKey,
  getFieldExampleKey,
} from "src/store/modelManagementSlice";

interface ValidationErrorModalProps {
  open: boolean;
  onClose: () => void;
  errors: Record<string, string>;
  model: Model;
}

interface ParsedError {
  context: string;
  message: string;
}

const MAX_VISIBLE_ERRORS = 10;

const parseErrors = (errors: Record<string, string>, model: Model): ParsedError[] => {
  const result: ParsedError[] = [];

  for (const [key, message] of Object.entries(errors)) {
    if (key === getModelNameKey(model.id) || key === getModelFieldKey(model.id)) {
      result.push({ context: "Model", message });
      continue;
    }

    const exampleSuffix = key.match(/_EXAMPLE_(\d+)(INPUT|OUTPUT)$/);
    const exampleIndex = exampleSuffix ? parseInt(exampleSuffix[1], 10) : -1;
    const field = Object.values(model.fields).find(
      (f) =>
        key === getFieldNameKey(f.id) ||
        key === getFieldDescriptionKey(f.id) ||
        key === getFieldTypeKey(f.id) ||
        key === getFieldOptionsKey(f.id) ||
        (exampleSuffix && key === getFieldExampleKey(f.id, exampleIndex, exampleSuffix[2])),
    );
    const fieldName = field?.name?.trim() || "Unnamed field";

    let inputLabel = "";
    if (field) {
      if (key === getFieldNameKey(field.id)) {
        inputLabel = "Field Name";
      } else if (key === getFieldDescriptionKey(field.id)) {
        inputLabel = "Description";
      } else if (key === getFieldTypeKey(field.id)) {
        inputLabel = "Type";
      } else if (key === getFieldOptionsKey(field.id)) {
        inputLabel = "Options";
      } else if (exampleSuffix) {
        inputLabel = `Example ${exampleIndex + 1} ${exampleSuffix[2] === "INPUT" ? "Input" : "Output"}`;
      }
    }

    result.push({
      context: `Field "${fieldName}"${inputLabel ? ` — ${inputLabel}` : ""}`,
      message,
    });
  }

  result.sort((a, b) => {
    if (a.context === "Model" && b.context !== "Model") return -1;
    if (a.context !== "Model" && b.context === "Model") return 1;
    return 0;
  });

  return result;
};

const ValidationErrorModal: React.FC<ValidationErrorModalProps> = ({
  open,
  onClose,
  errors,
  model,
}) => {
  const parsedErrors = useMemo(() => parseErrors(errors, model), [errors, model]);
  const total = parsedErrors.length;
  const visibleErrors = parsedErrors.slice(0, MAX_VISIBLE_ERRORS);
  const hiddenCount = total - visibleErrors.length;

  return (
    <Modal title="Failed to Save Model" open={open} onClose={onClose} width="520px">
      <Column spacing="300">
        {total > 0 ? (
          <>
            <Text type="b200" color="secondary">
              Please fix the following {total} issue{total !== 1 ? "s" : ""} before saving:
            </Text>
            <Column spacing="200">
              {visibleErrors.map((err, i) => (
                <Alert key={`${err.context}-${i}`} type="error" size="small">
                  <strong>{err.context}:</strong> {err.message}
                </Alert>
              ))}
              {hiddenCount > 0 && (
                <Text type="b200" color="secondary">
                  ...and {hiddenCount} more issue{hiddenCount !== 1 ? "s" : ""}. Fix the above
                  first.
                </Text>
              )}
            </Column>
          </>
        ) : (
          <Text type="b200" color="secondary">
            An unexpected validation error occurred. Please try again.
          </Text>
        )}
        <Row alignmentHorizontal="end">
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        </Row>
      </Column>
    </Modal>
  );
};

export default ValidationErrorModal;
